<template>
  <header class="topbar">
    <div class="topbar__brand">
      <el-dropdown trigger="click" @command="handleSystemCommand">
        <button
          type="button"
          class="topbar__brand-trigger"
          aria-label="切换系统"
        >
          <img class="brand-logo" src="/vone-logo.png" alt=""
          ><span class="brand-name">AI工作平台</span
          ><el-icon class="brand-chevron"><ArrowDown /></el-icon>
        </button>
        <template #dropdown
          ><el-dropdown-menu class="system-switcher-menu"
            ><el-dropdown-item disabled
              ><el-icon class="system-switcher-menu__current-icon"
                ><Check /></el-icon
              ><span class="system-switcher-menu__label">AI工作平台</span
              ><span class="system-switcher-menu__current">当前</span></el-dropdown-item
            ><el-dropdown-item
              v-if="aiBrainUrl"
              command="ai-brain"
              divided
              ><el-icon><Connection /></el-icon
              ><span class="system-switcher-menu__label">AI大脑平台</span
              ><el-icon class="system-switcher-menu__external"
                ><TopRight /></el-icon></el-dropdown-item
            ></el-dropdown-menu
          ></template
        >
      </el-dropdown>
    </div>
    <div class="topbar__context">
      <MobileAppMenu />
      <span class="topbar__utc mono">{{ utcTime }} UTC</span
      ><span class="topbar__divider"></span>
      <div class="topbar__context-item topbar__project">
        <span>当前项目</span
        ><el-select
          :model-value="context.currentProject?.id ?? ''"
          placeholder="全部授权项目"
          clearable
          @change="changeProject"
          ><el-option
            v-for="item in context.projects"
            :key="item.id"
            :label="item.name"
            :value="item.id"
        /></el-select>
      </div>
      <div class="topbar__context-item">
        <span>业务日期</span
        ><strong class="mono">{{ context.businessDate }}</strong>
      </div>
      <el-button
        v-if="auth.can('ai:analyze')"
        class="topbar__search"
        text
        aria-label="打开智能检索"
        @click="router.push('/ai/search')"
      >
        <el-icon><Search /></el-icon><span>智能检索</span>
      </el-button>
      <el-badge :value="unread" :hidden="!unread"
        ><el-button
          class="topbar__icon"
          text
          circle
          aria-label="通知"
          @click="router.push('/notifications')"
          ><el-icon><Bell /></el-icon></el-button
      ></el-badge>
      <el-dropdown @command="handleUserCommand"
        ><div class="topbar__user">
          <span class="user-avatar">{{ initial }}</span
          ><span>{{ auth.profile?.displayName }}</span
          ><el-icon><ArrowDown /></el-icon>
        </div>
        <template #dropdown
          ><el-dropdown-menu
            ><el-dropdown-item command="system" v-if="auth.isSystemAdmin"
              >系统管理</el-dropdown-item
            ><el-dropdown-item divided command="logout"
              >退出登录</el-dropdown-item
            ></el-dropdown-menu
          ></template
        ></el-dropdown
      >
    </div>
  </header>
</template>

<script setup lang="ts">
import {
  ArrowDown,
  Bell,
  Check,
  Connection,
  Search,
  TopRight,
} from "@element-plus/icons-vue";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import MobileAppMenu from "./MobileAppMenu.vue";
import { listNotifications } from "@/api/operations";
import { useAuthStore } from "@/stores/auth";
import { useSystemContextStore } from "@/stores/system-context";
import { normalizeExternalSystemUrl } from "@/utils/external-system-link";
dayjs.extend(utc);
const context = useSystemContextStore();
const auth = useAuthStore();
const router = useRouter();
const utcTime = ref(dayjs.utc().format("HH:mm:ss"));
const unread = ref(0);
const aiBrainUrl = normalizeExternalSystemUrl(
  import.meta.env.VITE_AI_BRAIN_URL,
);
const initial = computed(
  () => auth.profile?.displayName?.slice(0, 1).toUpperCase() ?? "U",
);
const timer = window.setInterval(
  () => (utcTime.value = dayjs.utc().format("HH:mm:ss")),
  1000,
);
async function loadUnread() {
  try {
    const response = await listNotifications();
    unread.value = response.data.filter(
      (item) => item.status === "unread",
    ).length;
  } catch {
    unread.value = 0;
  }
}
function changeProject(id: string) {
  context.selectProject(id || null);
  window.dispatchEvent(
    new CustomEvent("pms-project-change", { detail: id || null }),
  );
}
function handleSystemCommand(command: string) {
  if (command !== "ai-brain" || !aiBrainUrl) return;
  const openedWindow = window.open(
    aiBrainUrl,
    "_blank",
    "noopener,noreferrer",
  );
  if (openedWindow) openedWindow.opener = null;
}
async function handleUserCommand(command: string) {
  if (command === "system") await router.push("/system");
  if (command === "logout") {
    await auth.signOut();
    await router.replace("/login");
  }
}
onMounted(loadUnread);
onBeforeUnmount(() => window.clearInterval(timer));
</script>

<style scoped>
.topbar__brand-trigger {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
  padding: 5px 8px 5px 5px;
  border: 0;
  border-radius: 8px;
  color: #fff;
  background: transparent;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s ease;
}

.topbar__brand-trigger:hover,
.topbar__brand-trigger:focus-visible {
  background: rgba(255, 255, 255, 0.12);
  outline: none;
}

.brand-logo {
  width: 32px;
  height: 32px;
  flex: none;
  border-radius: 8px;
  object-fit: cover;
}

.brand-chevron {
  color: rgba(255, 255, 255, 0.68);
  font-size: 12px;
}

.system-switcher-menu {
  min-width: 220px;
}

.system-switcher-menu__current-icon {
  color: var(--el-color-primary);
}

.system-switcher-menu__label {
  flex: 1;
  min-width: 0;
  margin-right: 16px;
}

.system-switcher-menu__current {
  padding: 1px 7px;
  border-radius: 999px;
  color: var(--el-color-primary);
  background: var(--el-color-primary-light-9);
  font-size: 11px;
  line-height: 18px;
}

.system-switcher-menu__external {
  margin-left: auto;
  color: var(--el-text-color-placeholder);
}

.topbar__search {
  color: rgba(255, 255, 255, 0.9);
}

.topbar__search:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
}

@media (max-width: 960px) {
  .topbar__brand-trigger {
    gap: 6px;
    padding-right: 5px;
  }

  .topbar__search span {
    display: none;
  }
}
</style>
